// Per-turn transient state, reset at the start of every one of a player's
// turns (see turnActions.endTurn). Split into two kinds:
//  - bookkeeping the base rules need (usedAbilityInstanceIds for the
//    once-per-card-per-turn cap, leveledHeroInstanceIds, buysUsed)
//  - bonus pools that Milestone 3 ability handlers write into, because gold/
//    attack/light are otherwise purely derived from cards physically present
//    in hand (see attackMath.js, lightDarkness.js, selectors.js) and have
//    nowhere else to stash an ability-granted bonus like "Gain 2 gold" or
//    "Add Physical Attack +1 for each hero present".
export function createEmptyTurnFlags() {
  return {
    usedAbilityInstanceIds: [],
    // Numenera, Bandit Taskmaster/Bandit Warrior: "Global: You must use all
    // Dungeon abilities (if possible)." Distinct from usedAbilityInstanceIds
    // above: THAT one is deliberately left unpopulated for repeatable
    // abilities (so they stay re-triggerable), but "you must use it" is
    // satisfied by a single trigger regardless of repeatability - a
    // repeatable ability that's already fired once this turn shouldn't
    // force the obligation-checker to keep re-triggering it. Populated
    // unconditionally by every successful ability resolution
    // (actions/abilityActions.js's useHandCardAbility), read only by
    // effectRegistry/monsters.js's hasUnmetBanditObligation.
    triggeredAbilityInstanceIds: [],
    // Matt: "the actual rule... is that a hero that has already been leveled
    // up this turn can't be leveled up a second time... if I have two level
    // one Weathermages in my hand, I can... level them both." Tracks the
    // specific card INSTANCE produced by each level-up this turn (not the
    // hero's group/type) - so two distinct copies of the same named hero can
    // each be leveled once per turn, while a single card can't be chained
    // level 1 -> 2 -> 3 in one turn.
    leveledHeroInstanceIds: [],
    buysUsed: 0,
    bonusBuys: 0,
    bonusGold: 0,
    // Gold already committed to a purchase this Village visit - gold is a
    // shared per-turn budget (computeHandGoldValue), not a per-purchase
    // allowance, so a second buy (from bonusBuys) must be checked against
    // what's left, not the full total again. Never touched by free buys
    // (performBuy's requireGoldCheck: false, e.g. Bounty Hunter's Spoils).
    goldSpentThisVisit: 0,
    bonusPhysicalAttack: 0,
    bonusMagicAttack: 0,
    bonusLight: 0,
    lightForcedToZero: false, // e.g. Curse of Horror: "Reduce your Light to 0"
    // Applied once, after Darkness, to the *combined* Total Attack Value
    // (e.g. Curse of Sloth: "Lower your Total Attack Value by 3") - distinct
    // from bonusPhysicalAttack/bonusMagicAttack, which are pre-Darkness
    // subtotal adjustments and floor at 0 independently before combining.
    bonusTotalAttack: 0,
    dungeonAbilitiesLocked: false, // e.g. Mass Teleport: "no more Dungeon abilities this turn"
    allAbilitiesLocked: false, // e.g. Curse of Decay: "you cannot use any more abilities this turn" (unqualified - both Village and Dungeon)
    cannotEquipWeapons: false, // e.g. Curse of Horror/Sloth
    spoilsCancelled: false, // e.g. Marid: "Cancel all Spoils abilities"
    handSizeReduction: 0, // e.g. Curse of Shame: "draw 2 fewer cards"
    // Caverns of Bane, Historian: "Draw 1 additional card after you refill
    // your hand at the end of the turn." The positive counterpart to
    // handSizeReduction above - kept as a separate field rather than a
    // negative handSizeReduction so a stack trace/log reads as "bonus," not
    // a confusing negative reduction.
    bonusHandSizeAtEndOfTurn: 0,
    // e.g. Veilminder: "cancel one Battle or Aftermath effect on a monster in
    // the hall" - populated with monster instanceIds before that monster is
    // fought; checked by resolveCombat.js.
    cancelledBattleInstanceIds: [],
    cancelledAftermathInstanceIds: [],
    allBattleAftermathCancelled: false, // Veilminder L3: cancels every monster's Battle/Aftermath this turn
    // e.g. Deepstrider: "heroes gain Attack +1 against monsters of that
    // group" - only applies if the monster actually fought this turn belongs
    // to one of these groups (checked by attackMath.js against the chosen
    // monster's group, not applied unconditionally).
    groupConditionalAttackBonuses: [], // [{ group, amount, type: 'physical' | 'magic' }]
    // Numenera, Chemical Injector: "Add Magic Attack +2 to one hero against
    // Doomladen and Doomknights." Same shape as groupConditionalAttackBonuses
    // above, keyed by trait instead of group - see combat/attackMath.js.
    traitConditionalAttackBonuses: [], // [{ trait, amount, type: 'physical' | 'magic' }]
    // Into the Abyss, Bolt of Purity: "Magic Attack +1 against monsters
    // that give diseases." A narrower sibling of
    // groupConditionalAttackBonuses/traitConditionalAttackBonuses above -
    // only ever incremented by this one card, keyed off a derived property
    // (combat/attackMath.js's MONSTERS_THAT_GIVE_DISEASES) instead of a
    // printed group/trait, so it doesn't fit either existing list's shape.
    bonusMagicAttackAgainstDiseaseGivers: 0,
    // Numenera, Murden: "Trophy: If there is Darkness in this rank, add
    // Physical Attack +2 to each thief." Trophy resolves from hand at
    // Enter-the-Dungeon time, before any monster (and thus rank/Darkness)
    // is chosen - so, like Bolt of Purity above, the TRIGGER (Murden
    // present in hand) is unconditional and mandatory, but the CONDITION
    // ("if there is Darkness in this rank") is deferred: this flag just
    // marks Murden's Trophy as active for the rest of the turn, and
    // combat/attackMath.js's contributesTraits applies the actual +2 per
    // Thief using the real hasDarkness value for whichever rank ends up
    // being fought - more correct than evaluating it once at Trophy time,
    // since a player who fights more than once this turn could face
    // Darkness in one fight and not another.
    murdenTrophyActive: false,
    // Into the Abyss, Icon of Inversion: "Battle effects become Aftermath
    // effects." Set true by its Dungeon ability for the rest of the turn.
    // combat/resolveCombat.js's resolveTimedHandlers checks this: instead of
    // running a Battle-timed handler immediately, it pushes a plain
    // { defId, abilityIndex, instanceId, monsterInstanceId, hallRankIndex,
    // repeatCount } descriptor onto deferredBattleEffects and skips
    // execution; resolveCombat then flushes that queue (running each
    // deferred handler for real) right after win/loss is already decided,
    // alongside the fought monster's own Aftermath resolution.
    battleEffectsBecomeAftermath: false,
    deferredBattleEffects: [],
    // Worlds Collide, Undead Spectral group: "Immune to Physical Attack and
    // Magic Attack unless you [pay a cost] when you select this monster to
    // fight." Set to that monster's own definition id (not a boolean) by
    // combat/resolveCombat.js right before computeBaseAttack runs, per the
    // choices.payUndeadSpectralCost flag on CHOOSE_MONSTER - read back by
    // combat/attackMath.js's UNDEAD_SPECTRAL_IMMUNITY check.
    paidUndeadSpectralImmunity: null,
    pendingHeroReturns: [], // [{ instanceId, ownerId }] - e.g. Aird L3, resolved after the next combat
    // Root of Corruption, Elven Waybread: "Destroy this card. At the end of
    // this turn, place 1 hero on top of your deck." Same "recheck presence
    // at resolution time" shape as pendingHeroReturns, resolved in
    // actions/turnActions.js's endTurn instead - array (index 0 = topmost)
    // in case 2+ copies queue a placement in the same turn, an edge case
    // with no clear textual guidance on stacking order.
    pendingDeckPlacementInstanceIds: [],
    // Worlds Collide, Stoneguard Tanker/Juggernaut / Time Bend: "Add [this
    // card] to your hand at/after the end of the turn." Same shape as
    // pendingDeckPlacementInstanceIds just above (recheck hand presence at
    // resolution time), but lands directly in the new hand instead of the
    // top of the deck - see actions/turnActions.js's endTurn.
    keptForNextHandInstanceIds: [],
    // Worlds Collide, Ambrosia: "Whenever a Battle or Aftermath effect
    // destroys a hero, discard that hero instead." Set by its own Dungeon
    // ability - see actions/handChoiceActions.js's
    // heroDestroyRedirectsToDiscard (same choke point Guardian Blade's own
    // always-active, per-hero version of this redirect uses).
    battleAftermathDestroyRedirectsToDiscard: false,
    gainedDiseaseThisTurn: false, // set unconditionally by gainCurse - see Creeping Death (Worlds Collide)
    familiarUsedThisTurn: false, // set by any familiar ability use; read by turnActions.endTurn to decide whether to discard the familiar
    // Rulebook, "The Dungeon" (p.18): "you must choose a monster to fight" -
    // entering the dungeon commits you to fighting someone, unlike visiting
    // the village. Set by dungeonActions.chooseMonster once a fight actually
    // resolves; checked by actions/index.js's END_DUNGEON_TURN handler.
    foughtMonsterThisTurn: false,
    // Caverns of Bane, Tuath Trapper/Banisher: "Ignore Darkness penalties."
    // Also referenced by Owl Eyes' own inverse mechanic ("Ignore Darkness
    // penalty. Attack -2 for each point of Light.") - a distinct primitive
    // from lightForcedToZero (which changes total Light itself); this only
    // changes whether residual Darkness actually costs anything.
    ignoreDarknessPenalty: false,
    // Numenera, Mystic Glaive/Enhanced Mystic Glaive: "Convert this hero's
    // Physical Attack to Magic Attack." Per-instance (not per-player) since
    // only the specific card whose ability was used converts - see
    // combat/attackMath.js's contributesTraits.
    convertedToMagicInstanceIds: [],
    // Matt: "I used the thunderstone shard to give strength to a regular.
    // Then on a later turn that regular came out and still had the extra
    // strength on him. It should go away after the turn ends." Every
    // printed "Add Strength +N to a hero" ability (Thunderstone Shard,
    // Dwarven Ale, Bandias Wisdom, Ambrosia, Feast, etc.) is a Village/
    // Dungeon/Trophy-timed bonus with no "permanently" wording, so it
    // should only last for the turn - but cards/effectHelpers.js's
    // applyInstanceOverride (and the equivalent inline instances-spread
    // pattern several cards used directly) writes straight onto the card's
    // OWN instance record, which nothing ever reset. Tracks which
    // instances got a temporary Strength bump this turn (see
    // effectHelpers.js's grantTemporaryStrength) so actions/turnActions.js's
    // endTurn can strip the override back off, regardless of how many
    // times/cards stacked a bonus onto the same instance this turn.
    temporaryStrengthBoostInstanceIds: [],
    // Numenera's colored-XP rule (state/xpTokens.js): "A player can discard
    // one XP on their turn" - once per turn, free, independent of any card
    // (see actions/xpTokenActions.js).
    spentXpTokenThisTurn: false,
    // Root of Corruption, Discord Incarnate: "Trophy: Cards cost 1 extra
    // gold." A per-turn condition (Trophy effects re-apply every turn the
    // card is present in hand, see actions/villageActions.js's performBuy)
    // rather than a persistent effect - resets to 0 here like any other
    // turnFlag.
    extraGoldCostPerCard: 0,
    // Root of Corruption, War Incarnate: "Trophy: Ignore 1 hero with the
    // highest Strength." See cards/effectHelpers.js's markInstanceIgnored.
    ignoredInstanceIds: [],
    // Root of Corruption, Rage of the Disowned: "One hero adds Physical
    // Attack +5 and cannot be discarded or destroyed by Dungeon abilities or
    // Battle effects." Populated by its own Dungeon ability
    // (effectRegistry/villages.js); consulted by cards/effectHelpers.js's
    // battleTargetableHeroes (the Battle-effect half of the protection) and
    // read/cleared by the same card's own Aftermath ("Destroy that hero").
    protectedHeroInstanceIds: [],
    // Root of Corruption, Tincture of Victims: "Convert all Magic Attack to
    // Physical Attack, or vice versa." Set by its Dungeon ability (before a
    // monster is even chosen); consumed later by combat/attackMath.js's
    // computeBaseAttack, once the party's Physical/Magic subtotals actually
    // exist - same "grant now, consume once the total is known" shape as
    // bonusPhysicalAttack/ignoreDarknessPenalty, not a special case.
    attackConversionDirection: null, // null | 'toPhysical' | 'toMagic'
    // Root of Corruption, Rockslide: "Move this monster to the highest rank
    // (once per turn)." Breach can in principle fire more than once for the
    // same monster instance within a single turn (e.g. Greatsword's
    // deferred multi-refill, or Mind Control's own immediate refill
    // followed by a later normal-combat one) - this caps just the MOVE
    // clause per instance; "destroy the top of each hero stack" (the rest
    // of the same Breach text) has no such cap and still fires every time.
    // Tracked on the acting player who caused the refill, same scoping as
    // every other once-per-turn cap in this file.
    hallRepositionedInstanceIds: [],
  };
}
